import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"
import { positionFixedMenu } from "../lib/anchored_menu_position"

// Renders a dropdown of merge-field tokens grouped by category. Clicking a
// field inserts `{{token}}` at the cursor position of the connected
// `textarea` target (single-line/plain surfaces — e.g. the support-admin
// template Subject field). Rich-text bodies live in the shared composer
// island, which carries its own InsertFieldMenu; this controller only serves
// the remaining plain inputs.
//
// Usage:
//   .col-md-12{ data: { controller: 'merge-field-picker',
//                       'merge-field-picker-url-value': settings_templates_merge_fields_path } }
//     .merge-field-picker.merge-field-picker-toolbar
//       %button{ data: { action: 'click->merge-field-picker#toggle',
//                        'merge-field-picker-target': 'button' } }
//         Insert merge field
//       .merge-field-picker-menu{ hidden: true,
//                                  data: { 'merge-field-picker-target': 'menu' } }
//     = form.text_area :body, data: { 'merge-field-picker-target': 'textarea' }
export default class extends Controller {
  static targets = ["button", "menu", "textarea"]
  static values = { url: String }

  connect() {
    this.boundDocumentClick = this.handleDocumentClick.bind(this)
    document.addEventListener("click", this.boundDocumentClick)
  }

  disconnect() {
    document.removeEventListener("click", this.boundDocumentClick)
  }

  async toggle(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.isOpen()) {
      this.close()
    } else {
      await this.open()
    }
  }

  async open() {
    // Show the menu immediately with a loading placeholder — a click that
    // visibly does nothing while the fetch is in flight invites re-clicking.
    if (!this.fieldsLoaded) this.renderLoading()
    this.positionMenu()
    this.menuTarget.hidden = false

    // Memoize the in-flight fetch so two rapid clicks don't fire two requests.
    // On failure, `loadFields` clears `_loadPromise` so a subsequent click
    // retries instead of replaying the failed promise.
    if (!this.fieldsLoaded && !this._loadPromise) {
      this._loadPromise = this.loadFields()
    }
    if (this._loadPromise) {
      await this._loadPromise
    }
    if (this.fieldsLoaded) {
      // Re-anchor now that the real field list replaced the placeholder.
      this.positionMenu()
    } else {
      // Failed load: hide rather than leave a stuck "Loading…" menu —
      // fieldsLoaded is only set after a successful render.
      this.close()
    }
  }

  renderLoading() {
    this.menuTarget.innerHTML = ""

    const title = document.createElement("div")
    title.className = "gl-notif-merge-title"
    title.textContent = "Insert field"
    this.menuTarget.appendChild(title)

    const loading = document.createElement("div")
    loading.className = "gl-notif-merge-group"
    loading.setAttribute("role", "status")
    loading.textContent = "Loading…"
    this.menuTarget.appendChild(loading)
  }

  // Anchor the popover to the trigger button via viewport coordinates so
  // ancestor `overflow: hidden` (on the card/section wrapping the textarea)
  // can't clip it, flipping above the button when it sits near the viewport
  // bottom.
  positionMenu() {
    const rect = this.buttonTarget.getBoundingClientRect()
    const menu = this.menuTarget
    positionFixedMenu(menu, rect, { measureHidden: true })
    menu.style.zIndex = "var(--gl-zindex-1090)"
  }

  close() {
    this.menuTarget.hidden = true
  }

  isOpen() {
    return !this.menuTarget.hidden
  }

  handleDocumentClick(event) {
    if (!this.isOpen()) return
    if (this.element.contains(event.target)) return
    this.close()
  }

  async loadFields() {
    try {
      const response = await get(this.urlValue, { responseKind: "json" })
      if (!response.ok) {
        // Non-ok HTTP (4xx/5xx) — clear the cached promise so the next click
        // retries instead of getting stuck on a settled-but-failed promise.
        this._loadPromise = null
        return
      }

      const json = await response.response.json()
      this.renderFields(json.categories || [])
      this.fieldsLoaded = true
    } catch (_error) {
      // Network error / timeout / CORS — silently keep the menu hidden so the
      // agent can retry by clicking again. Clear the cached promise so the
      // next click triggers a fresh fetch instead of replaying the rejection.
      this._loadPromise = null
    }
  }

  renderFields(categories) {
    this.menuTarget.innerHTML = ""

    const title = document.createElement("div")
    title.className = "gl-notif-merge-title"
    title.textContent = "Insert field"
    this.menuTarget.appendChild(title)

    const list = document.createElement("div")
    list.className = "gl-notif-merge-list"

    categories.forEach((group) => {
      const heading = document.createElement("div")
      heading.className = "gl-notif-merge-group"
      heading.textContent = group.category
      list.appendChild(heading)

      group.fields.forEach((field) => {
        const item = document.createElement("button")
        item.type = "button"
        item.className = "gl-notif-merge-item"
        item.dataset.token = field.token
        item.dataset.action = "click->merge-field-picker#select"
        // Registry fields may carry ready-made rich content instead of a
        // `{{token}}` (e.g. the signature picker's "Download VCF Card"
        // anchor). Stash it on the item; #select branches on its presence.
        if (field.insert_html) item.dataset.insertHtml = field.insert_html

        const label = document.createElement("span")
        label.className = "gl-notif-merge-item-label"
        label.textContent = field.label
        item.appendChild(label)

        const token = document.createElement("code")
        token.className = "gl-notif-merge-item-token"
        // For insert_html fields, previewing "{{agent_vcf_card}}" would lie —
        // no token is inserted — so preview the content's visible text instead.
        token.textContent = field.insert_html
          ? this.htmlPreviewText(field.insert_html)
          : `{{${field.token}}}`
        item.appendChild(token)

        list.appendChild(item)
      })
    })

    this.menuTarget.appendChild(list)
  }

  select(event) {
    event.preventDefault()
    event.stopPropagation()
    const { token, insertHtml } = event.currentTarget.dataset
    if (insertHtml) {
      this.insertHtml(insertHtml)
    } else if (token) {
      this.insertToken(token)
    }
    this.close()
  }

  // Text-only preview of an insert_html field for the dropdown row (e.g.
  // "<a href=…>Download VCF Card</a>" → "Download VCF Card").
  htmlPreviewText(html) {
    const container = document.createElement("div")
    container.innerHTML = html
    return container.textContent.trim()
  }

  // Rich-content path for registry fields carrying insert_html. Rich-text
  // bodies now live in the shared composer island (with its own picker), so
  // the remaining single-line surfaces get the visible text — a misconfigured
  // surface degrades to something sensible rather than raw markup.
  insertHtml(html) {
    const text = this.htmlPreviewText(html)
    if (this.hasTextareaTarget) {
      this.insertIntoTextarea(text)
    }
  }

  insertToken(token) {
    const insertion = `{{${token}}}`
    if (this.hasTextareaTarget) {
      this.insertIntoTextarea(insertion)
    }
  }

  insertIntoTextarea(insertion) {
    const textarea = this.textareaTarget
    const start = textarea.selectionStart ?? textarea.value.length
    const end = textarea.selectionEnd ?? textarea.value.length

    if (typeof textarea.setRangeText === "function") {
      textarea.setRangeText(insertion, start, end, "end")
    } else {
      textarea.value = textarea.value.slice(0, start) + insertion + textarea.value.slice(end)
      textarea.selectionStart = textarea.selectionEnd = start + insertion.length
    }
    textarea.focus()
    textarea.dispatchEvent(new Event("input", { bubbles: true }))
  }
}
